/**
 * Render "@shortname" inside a rendered post as a club mention chip, using
 * core's own GroupMention classes so it looks exactly like a group mention.
 *
 * The post body is server-rendered HTML that Mithril injects as trusted
 * content, so this works on the DOM after render. It only wraps plain text
 * nodes and never touches code, links or existing mentions; running it again
 * on an already decorated post changes nothing.
 *
 * No lookbehind in the pattern: a lookbehind literal is a parse-time
 * SyntaxError on Safari/iOS before 16.4 and would break the whole bundle.
 */

const SKIP = 'code, pre, a, .ClubMention, .UserMention, .GroupMention, .PostMention, .TagMention';

function escapeRegExp(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * @param {HTMLElement} root The post body element.
 * @param {{slug: string, color: ?string, icon: ?string}} club
 */
export default function decorateClubMentions(root, club) {
  if (!root || !club || !club.slug) return;

  // Same boundaries as ClubMentionParser.php: not glued to a word or another
  // "@" before, not followed by more name characters after.
  const pattern = new RegExp(`(^|[^\\p{L}\\p{N}_@])@(${escapeRegExp(club.slug)})(?![\\p{L}\\p{N}_]|-[\\p{L}\\p{N}])`, 'giu');

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || node.nodeValue.indexOf('@') === -1) return NodeFilter.FILTER_REJECT;

      return node.parentElement && node.parentElement.closest(SKIP) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
    },
  });

  const nodes = [];

  while (walker.nextNode()) nodes.push(walker.currentNode);

  nodes.forEach((node) => replaceInTextNode(node, pattern, club));
}

function replaceInTextNode(node, pattern, club) {
  const text = node.nodeValue;
  const fragment = document.createDocumentFragment();
  let last = 0;
  let match;

  pattern.lastIndex = 0;

  while ((match = pattern.exec(text)) !== null) {
    const start = match.index + match[1].length;

    if (start > last) fragment.appendChild(document.createTextNode(text.slice(last, start)));

    fragment.appendChild(chip(match[2], club));
    last = start + 1 + match[2].length;
  }

  if (last === 0) return;

  if (last < text.length) fragment.appendChild(document.createTextNode(text.slice(last)));

  node.parentNode.replaceChild(fragment, node);
}

function chip(name, club) {
  const span = document.createElement('span');

  span.className = 'GroupMention ClubMention' + (club.color ? ' GroupMention--colored' : '');
  if (club.color) span.style.setProperty('--color', club.color);

  const label = document.createElement('span');
  label.className = 'GroupMention-name';
  label.textContent = '@' + name;
  span.appendChild(label);

  const icon = document.createElement('i');
  icon.className = 'icon ' + (club.icon || 'fas fa-users');
  icon.setAttribute('aria-hidden', 'true');
  span.appendChild(icon);

  return span;
}
